// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
export const getValidateAmountMessage =
  (action: string, currBalance?: string) => (inputAmount: string) => {
    if (currBalance === undefined) {
      return '';
    }
    const input = parseFloat(inputAmount);
    const currentBalance = parseFloat(currBalance);
    if (input > currentBalance) {
      return `${action} amount exceeds current holding balance`;
    }
    return '';
  };

export type ValidationResult = {
  isValid: boolean;
  message: string;
};

const validResult = (): ValidationResult => ({ isValid: true, message: '' });

const invalidResult = (message: string): ValidationResult => ({
  isValid: false,
  message,
});

export const validateMintAmount = (value: string, mintLimit: number): ValidationResult => {
  if (value.trim() === '') {
    return validResult();
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return invalidResult('Ensure value is a number');
  }
  if (parsed < 0) {
    return invalidResult('Ensure value is positive');
  }
  if (parsed === 0) {
    return invalidResult('Ensure value is greater than zero');
  }
  if (parsed > mintLimit) {
    return invalidResult(`Mint amount cannot exceed upper limit of ${mintLimit}`);
  }
  return validResult();
};

export const validateBurnAmount =
  (burnLimit: number, currBalance?: string) =>
  (inputAmount: string): ValidationResult => {
    if (inputAmount.trim() === '') {
      return validResult();
    }

    const parsed = Number(inputAmount);
    if (!Number.isFinite(parsed)) {
      return invalidResult('Ensure value is a number');
    }
    if (parsed < 0) {
      return invalidResult('Ensure value is positive');
    }
    if (parsed === 0) {
      return invalidResult('Ensure value is greater than zero');
    }
    if (parsed > burnLimit) {
      return invalidResult(`Burn amount cannot exceed upper limit of ${burnLimit}`);
    }

    const message = getValidateAmountMessage('Burn', currBalance)(inputAmount);

    return message.length > 0 ? invalidResult(message) : validResult();
  };
